<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import ArrowLeftIcon from 'vue-material-design-icons/ArrowLeft.vue'
import HomeOutlineIcon from 'vue-material-design-icons/HomeOutline.vue'

/**
 * The ascent ("up") cluster. Its anchor is the ascent control — styled as an
 * icon button but a real `RouterLink` (an <a>), never a <button> wrapped in an
 * anchor — because ascent is deterministic navigation to a named parent, so it
 * must be a link the browser treats as one (middle-click, cmd-click, focusable
 * href). It points at a resolved parent destination, not browser history: the
 * same target however the user arrived. Its label names that destination for
 * screen readers (e.g. "Back to Characters") rather than a generic arrow.
 *
 * Beside the arrow sits a recessive **home** reveal — the left-side mirror of
 * the home view's avatar→cog disclosure. The arrow is the persistent anchor
 * (parent ascent); on the cluster's hover/focus a home link grows inboard to
 * its right, giving the "all the way out" escape the design rules ask for
 * (immersion still requires a guaranteed exit: home, *and* ascent to the
 * parent). It is always present wherever the cluster renders; the shell only
 * mounts the cluster where ascent itself is shown.
 */
const props = defineProps<{
  to: RouteLocationRaw
  label?: string
}>()
</script>

<template>
  <div class="chrome-ascent-cluster">
    <RouterLink
      :to="props.to"
      class="chrome-ascent"
      :aria-label="props.label ? `Back to ${props.label}` : 'Back'"
    >
      <ArrowLeftIcon class="chrome-ascent__icon" aria-hidden="true" />
    </RouterLink>
    <RouterLink :to="{ name: 'home' }" class="chrome-home" aria-label="Home">
      <HomeOutlineIcon class="chrome-home__icon" aria-hidden="true" />
    </RouterLink>
  </div>
</template>

<style scoped>
.chrome-ascent-cluster {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.chrome-ascent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  color: var(--theme-text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 150ms, color 150ms;
}

.chrome-ascent:hover {
  color: var(--theme-on-surface, rgba(255, 255, 255, 0.9));
  background-color: var(--theme-surface-interactive, rgba(255, 255, 255, 0.07));
}

.chrome-ascent:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

.chrome-ascent__icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chrome-ascent__icon :deep(svg) {
  width: 100%;
  height: 100%;
}

/* The home reveal: collapsed by default, grows inboard (to the right of the
   arrow) on the cluster's hover/focus — the left-side mirror of the home-view
   cog. Kept keyboard-reachable: focus-within expands it so a Tab lands on a
   visible target. */
.chrome-home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 36px;
  padding: 0;
  overflow: hidden;
  opacity: 0;
  border-radius: 9999px;
  color: var(--theme-text-secondary);
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 150ms, color 150ms, opacity 150ms, width 180ms ease;
}

.chrome-ascent-cluster:hover .chrome-home,
.chrome-ascent-cluster:focus-within .chrome-home {
  width: 36px;
  opacity: 1;
}

.chrome-home:hover {
  color: var(--theme-on-surface, rgba(255, 255, 255, 0.9));
  background-color: var(--theme-surface-interactive, rgba(255, 255, 255, 0.07));
}

.chrome-home:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

.chrome-home__icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chrome-home__icon :deep(svg) {
  width: 100%;
  height: 100%;
}
</style>
